import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { Container, OptionList, OptionStyled, SearchContainer } from './DropdownMenu.styled';
import { MagnifyingGlass } from 'assets/icons/Icons';
import { TextInput } from 'components/v2/Inputs/TextInput/TextInput';
import { escapeRegex } from 'kb-shared/utilities/regex';

import { Props, Option } from './DropdownMenu.types';

export const DropdownMenu = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    id,
    searchable,
    searchPlaceholder,
    onSelect,
    onClose,
    searchFn,
    selected,
    options,
    positionStatic,
    maxMenuHeight
  } = props;
  const isSelected = (option: Option): boolean => {
    return (
      selected?.some(
        selectedOption =>
          selectedOption.value === option.value && selectedOption.label === option.label
      ) ?? false
    );
  };

  const [value, setValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [activeOptionIndex, setActiveOptionIndex] = useState<number | null>(null);
  const optionListRef = useRef<HTMLDivElement>(null);
  const searchInputId = `input-dropdown-search-${id}`;
  const optionId = (optionIndex: number) => `${id}-option-${optionIndex}`;

  const focusSearchInput = () => {
    const searchInput = document.getElementById(searchInputId) as HTMLInputElement | null;
    searchInput?.focus();
  };

  const focusOptionAtIndex = (optionIndex: number) => {
    const option = document.getElementById(optionId(optionIndex));
    option?.focus();
  };

  const scrollOptionIntoView = (optionIndex: number) => {
    const option = document.getElementById(optionId(optionIndex));
    if (option && 'scrollIntoView' in option && typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  };

  useEffect(() => {
    // Focus the search input for searchable dropdowns, otherwise focus the selected option or first option.
    const timer = setTimeout(() => {
      if (searchable) {
        focusSearchInput();
        return;
      }

      const selectedEl = optionListRef.current?.querySelector(
        '[aria-selected="true"]'
      ) as HTMLElement;
      const firstEl = optionListRef.current?.querySelector('[role="option"]') as HTMLElement;
      (selectedEl || firstEl)?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [searchable]);

  useEffect(() => {
    if (!searchable) return;

    if (filteredOptions.length === 0) {
      setActiveOptionIndex(null);
      return;
    }

    const selectedOptionIndex = filteredOptions.findIndex(option => isSelected(option));
    setActiveOptionIndex(selectedOptionIndex >= 0 ? selectedOptionIndex : null);
  }, [filteredOptions, searchable]);

  useEffect(() => {
    if (!searchable || activeOptionIndex === null) return;

    scrollOptionIntoView(activeOptionIndex);
  }, [activeOptionIndex, searchable]);

  const search = (term: string): void => {
    if (!term) return setFilteredOptions(options);

    if (searchFn) {
      const filteredByTerm = searchFn(options, term);
      setFilteredOptions(filteredByTerm);
    } else {
      const regex = new RegExp(`.*${escapeRegex(term)}.*`, 'i');
      const filteredByTerm = options.filter(option => regex.test(option.label));
      setFilteredOptions(filteredByTerm);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveOptionIndex(currentIndex => {
        if (filteredOptions.length === 0) return null;
        if (currentIndex === null) return 0;
        return Math.min(currentIndex + 1, filteredOptions.length - 1);
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveOptionIndex(currentIndex => {
        if (filteredOptions.length === 0) return null;
        if (currentIndex === null) return 0;
        return Math.max(currentIndex - 1, 0);
      });
    } else if ((e.key === 'Enter' || e.key === ' ') && activeOptionIndex !== null) {
      e.preventDefault();
      onSelect(filteredOptions[activeOptionIndex]);
    } else if (e.key === 'Tab') {
      requestAnimationFrame(() => {
        onClose?.();
      });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.({ restoreFocus: true });
    }
  };

  const handleOptionKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    option: Option,
    currentIndex: number
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusOptionAtIndex(currentIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();

      if (currentIndex === 0 && searchable) {
        focusSearchInput();
        return;
      }

      focusOptionAtIndex(currentIndex - 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(option);
    } else if (e.key === 'Tab') {
      requestAnimationFrame(() => {
        onClose?.();
      });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.({ restoreFocus: true });
    }
  };

  return (
    <Container ref={ref} $positionStatic={positionStatic} $maxMenuHeight={maxMenuHeight}>
      {searchable && (
        <SearchContainer>
          <TextInput
            id={searchInputId}
            role="combobox"
            leftIcon={<MagnifyingGlass type="solid" />}
            type="search"
            placeholder={searchPlaceholder}
            value={value}
            ariaLabel={searchPlaceholder || 'Search options'}
            ariaControls={id}
            ariaExpanded={true}
            ariaAutocomplete="list"
            ariaHasPopup="listbox"
            ariaActiveDescendant={
              activeOptionIndex === null ? undefined : optionId(activeOptionIndex)
            }
            onKeyDown={handleSearchKeyDown}
            onChange={e => {
              setValue(e.currentTarget.value);
              search(e.currentTarget.value);
            }}
          />
        </SearchContainer>
      )}

      <OptionList ref={optionListRef} id={id} role="listbox">
        {filteredOptions.map((option, index) => (
          <OptionStyled
            onClick={() => onSelect(option)}
            onKeyDown={searchable ? undefined : e => handleOptionKeyDown(e, option, index)}
            onMouseEnter={() => {
              if (searchable) {
                setActiveOptionIndex(index);
              }
            }}
            id={optionId(index)}
            key={`${option.value} ${option.label}`}
            aria-selected={isSelected(option)}
            $isActive={searchable && activeOptionIndex === index}
            tabIndex={searchable ? undefined : -1}
          >
            {option.label}
          </OptionStyled>
        ))}
      </OptionList>
    </Container>
  );
});

// must be set because of forwardRef otherwise eslint complains
DropdownMenu.displayName = 'DropdownMenu';
